import { Button, Input, ModalProps, Pagination } from 'antd';
import { Dispatch, SetStateAction, memo, useEffect, useState } from 'react';
import { updateItemHighlightsByItems } from './share';
import { EditOutlined } from '@ant-design/icons';
import { useStoreNameConfig } from 'hooks/business/store/useStoreName';
import { useItemSelectContext } from './ItemSelectProvider';
import { Preview, stores2storeScopes } from '../share';
import { item2selectItem, selectItem2item } from './Items';
import { noop } from 'lodash-es';
import styled from '@emotion/styled';
import Item from '@knowledge/common/components/Item';
import { JustRightHeightModal } from 'components/styled/scoped';
import { useTranslate } from '@tolgee/react';

const pageSize = 5;

export interface ItemHighlightsModalProps extends ModalProps {
  itemLabel: string;
  recommendItems: Knowledge.Activity.Item[];
  itemHighlights: Knowledge.Activity.ItemHighlight[];
  onEditItems: () => void;
  onItemHighlightsChange: (itemHighlights: Knowledge.Activity.ItemHighlight[]) => void;
}

function ItemHighlightsModal(props: ItemHighlightsModalProps) {
  const {
    itemLabel,
    recommendItems,
    itemHighlights,
    onEditItems,
    onItemHighlightsChange,
    onCancel,
    ...otherProps
  } = props;

  const { t } = useTranslate();

  const [editValue, setEditValue] = useState(itemHighlights);
  const [page, setPage] = useState(1);
  const currentPageValue = editValue.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => {
    const maxPage = Math.ceil(editValue.length / pageSize);
    if (maxPage > 0) {
      setPage((page) => Math.min(page, maxPage));
    }
  }, [editValue]);

  useEffect(() => {
    setEditValue((editValue) =>
      updateItemHighlightsByItems(
        itemHighlights.reduce((res, { appliedItems }) => {
          res.push(...appliedItems);
          return res;
        }, [] as Knowledge.Activity.Item[]),
        editValue,
      ),
    );
  }, [itemHighlights]);

  const editList = currentPageValue.map((itemHighlight, curIndex) => {
    const index = curIndex + (page - 1) * pageSize;
    return (
      <SingleEdit
        itemHighlight={itemHighlight}
        index={index}
        recommendItems={recommendItems}
        setEditValue={setEditValue}
      />
    );
  });

  const pagination = (
    <Pagination
      size="small"
      current={page}
      pageSize={pageSize}
      total={editValue.length}
      onChange={setPage}
    />
  );

  return (
    <JustRightHeightModal
      width={890}
      title={t('添加商品亮点')}
      okText={t('marketing.保存')}
      onOk={(e) => {
        onItemHighlightsChange(editValue);
        onCancel?.(e);
      }}
      onCancel={onCancel}
      {...otherProps}
    >
      <Container>
        <div>
          <span>{itemLabel}</span>
          {t(`knowledge.x 个商品${recommendItems.length > 1 ? '_other' : ''}`, {
            x: recommendItems.length,
          })}
          <Button type="text" icon={<EditOutlined />} onClick={onEditItems} />
        </div>
        <div>
          {t('商品亮点')}
          <span>
            {t('添加商品亮点后，小乐将会根据每个的亮点生成最具吸引力的话术，提升活动推广效果。')}
          </span>
        </div>
        <div>
          <div>{t('活动机器人-商品')}</div>
          <div>{t('商品亮点')}</div>
        </div>
        <section>{editList}</section>
        {pagination}
      </Container>
    </JustRightHeightModal>
  );
}

const SingleEdit = memo(
  ({
    itemHighlight: { highlight, appliedItems },
    index,
    recommendItems,
    setEditValue,
  }: {
    itemHighlight: Knowledge.Activity.ItemHighlight;
    index: number;
    recommendItems: Knowledge.Activity.Item[];
    setEditValue: Dispatch<SetStateAction<Knowledge.Activity.ItemHighlight[]>>;
  }) => {
    const { t } = useTranslate();
    const { configMap } = useStoreNameConfig();
    const { show } = useItemSelectContext();

    if (!appliedItems.length) {
      return null;
    }

    const [mainItem, ...otherItems] = appliedItems;

    const onShowItemSelect = ({
      selectableItems,
      ...others
    }: {
      selectableItems: Knowledge.Activity.Item[];
    } & Pick<Required<Parameters<typeof show>>[0], 'value' | 'onChange'>) =>
      show({
        storeType: 'ALL',
        storeScopes: stores2storeScopes(selectableItems),
        skuDisabled: true,
        sideStoreEnable: true,
        defaultFoldSku: true,
        internalItemId: selectableItems.map(({ itemInternalId }) => String(itemInternalId)),
        onCancel: noop,
        ...others,
      });

    const onCombineItems = () =>
      onShowItemSelect({
        selectableItems: recommendItems.filter(
          ({ itemInternalId }) => itemInternalId !== mainItem.itemInternalId,
        ),
        value: otherItems.map(item2selectItem),
        onChange: (value) => {
          const nextItems = value.map(selectItem2item);
          const addedItems = nextItems.filter(({ itemInternalId }) => {
            return otherItems.every((item) => item.itemInternalId !== itemInternalId);
          });
          const removedItems = otherItems.filter(({ itemInternalId }) => {
            return nextItems.every((item) => item.itemInternalId !== itemInternalId);
          });

          if (addedItems.length === 0 && removedItems.length === 0) return;

          setEditValue((editValue) =>
            editValue
              .map(({ highlight, appliedItems }, _index) => {
                return {
                  highlight,
                  appliedItems:
                    _index === index
                      ? [mainItem, ...nextItems]
                      : appliedItems.filter(({ itemInternalId }) =>
                          addedItems.every((item) => item.itemInternalId !== itemInternalId),
                        ),
                };
              })
              .filter(({ appliedItems }) => appliedItems.length)
              .concat(
                removedItems.map((item) => ({
                  highlight,
                  appliedItems: [item],
                })),
              ),
          );
        },
      });

    const onReuseHighlight = () =>
      onShowItemSelect({
        selectableItems: recommendItems.filter(({ itemInternalId }) => {
          return appliedItems.every((item) => item.itemInternalId !== itemInternalId);
        }),
        value: [],
        onChange: (value) => {
          if (!value.length) return;

          const reusedItems = value.map(selectItem2item);

          setEditValue((editValue) =>
            editValue.reduce((res, cur) => {
              const targetItems = cur.appliedItems.filter(({ itemInternalId }) => {
                return reusedItems.some((item) => item.itemInternalId === itemInternalId);
              });

              if (targetItems.length === 0) {
                res.push(cur);
              } else {
                if (targetItems.length < cur.appliedItems.length) {
                  res.push({
                    ...cur,
                    appliedItems: cur.appliedItems.filter(({ itemInternalId }) => {
                      return targetItems.every((item) => item.itemInternalId !== itemInternalId);
                    }),
                  });
                }
                targetItems.forEach((item) => {
                  res.push({
                    highlight,
                    appliedItems: [item],
                  });
                });
              }

              return res;
            }, [] as Knowledge.Activity.ItemHighlight[]),
          );
        },
      });

    const itemRender = (item: Knowledge.Activity.Item) => {
      const { platform, region, shopName, shopAlias, spuId, sellerSpu } = item;
      const isStoreAliasFirst = configMap?.[platform]?.[region];
      const name = isStoreAliasFirst ? shopAlias || shopName : shopName;
      const extraStr = `${name} | ${spuId} | ${sellerSpu || '-'}`;
      return (
        <Item
          width={56}
          titleLine={2}
          item={item}
          extra={
            <div
              className="line-clamp-1"
              style={{ color: 'var(--fett-text-tertiary)' }}
              title={extraStr}
            >
              {extraStr}
            </div>
          }
        />
      );
    };

    return (
      <SingleContainer>
        <section>
          <div>{itemRender(mainItem)}</div>
          <div>
            <Input.TextArea
              showCount
              maxLength={200}
              placeholder={t(
                '请输入商品亮点，例如商品造型时尚、技术先进、相较于上一款商品的优点等',
              )}
              value={highlight}
              onChange={({ target: { value } }) =>
                setEditValue((editValue) => [
                  ...editValue.slice(0, index),
                  {
                    highlight: value,
                    appliedItems,
                  },
                  ...editValue.slice(index + 1),
                ])
              }
            />
          </div>
        </section>
        <div>
          <section>
            {otherItems.length === 0 ? (
              <Button type="link" disabled={recommendItems.length <= 1} onClick={onCombineItems}>
                {t('关联同款商品')}
              </Button>
            ) : (
              <div>
                {t('更多x个同款商品', { x: otherItems.length })}
                <Preview
                  content={
                    <ItemList>
                      {otherItems.map((item) => (
                        <li key={item.itemInternalId}>{itemRender(item)}</li>
                      ))}
                    </ItemList>
                  }
                />
                <Button type="text" icon={<EditOutlined />} onClick={onCombineItems} />
              </div>
            )}
          </section>
          <section>
            <Button
              type="link"
              disabled={!highlight.trim() || recommendItems.length - appliedItems.length <= 0}
              onClick={onReuseHighlight}
            >
              {t('将亮点复用至其他商品')}
            </Button>
          </section>
        </div>
      </SingleContainer>
    );
  },
);

const SingleContainer = styled.div`
  border: solid 1px var(--fett-border-default);
  & + & {
    border-top: none;
  }
  > section,
  > div {
    display: flex;
    align-items: stretch;
    > * {
      flex: 1 0 0;
      overflow: hidden;
      &:first-of-type {
        border-right: solid 1px var(--fett-border-default);
      }
    }
  }
  > section {
    border-bottom: solid 1px var(--fett-border-default);
    > div {
      display: flex;
      align-items: center;
      padding: 12px;
      .ant-input-textarea {
        width: 100%;
        textarea {
          resize: none;
        }
      }
    }
  }
  > div > section {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    > div {
      display: flex;
      align-items: center;
    }
  }
`;

const ItemList = styled.ul`
  height: 300px;
  width: 420px;
  margin: -12px -16px;
  padding: 12px;
  list-style: none;
  overflow: auto;
  > li {
    margin-bottom: 12px;
    padding: 12px;
    border: solid 1px var(--fett-border-default);
    border-radius: 2px;
  }
`;

const Container = styled.div`
  margin: -24px;
  padding: 14px;
  > .ant-pagination {
    margin-top: 14px;
    text-align: right;
  }
  > div:first-of-type {
    display: flex;
    align-items: center;
    margin-bottom: 14px;
    padding: 4px 14px;
    border: solid 1px var(--fett-border-default);
    > span {
      margin-right: 14px;
    }
  }
  > div:nth-child(2) {
    display: flex;
    align-items: center;
    color: var(--fett-text-primary);
    > span {
      flex: 1 0 0;
      margin-left: 12px;
      font-size: 12px;
      font-weight: 400;
      color: var(--fett-text-tertiary);
    }
  }
  > div:nth-child(3) {
    display: flex;
    align-items: center;
    margin: 8px 0;
    background: var(--fett-erp-card-bg);
    > * {
      flex: 1 0 0;
      padding: 10px 14px;
    }
  }
`;

export default ItemHighlightsModal;
